import {
  BusinessEntityType,
  RequestedLimitSource,
  SupplementalDataType,
  UnderwritingStatus,
} from './enums'

export const UNDERWRITING_STATUS_LABELS: Record<UnderwritingStatus, string> = {
  [UnderwritingStatus.Processing]: 'With the lender',
  [UnderwritingStatus.ManualReview]: 'Manual review',
  [UnderwritingStatus.Approved]: 'Approved',
  [UnderwritingStatus.Declined]: 'Declined',
  [UnderwritingStatus.Error]: 'Lender error',
}

export const ENTITY_TYPE_LABELS: Record<BusinessEntityType, string> = {
  [BusinessEntityType.Llc]: 'LLC',
  [BusinessEntityType.SoleProprietor]: 'Sole proprietor',
  [BusinessEntityType.Partnership]: 'Partnership',
  [BusinessEntityType.Corporation]: 'Corporation',
  [BusinessEntityType.Cooperative]: 'Cooperative',
  [BusinessEntityType.NonProfit]: 'Non-profit',
  [BusinessEntityType.NotSure]: 'Not sure',
  [BusinessEntityType.NotRegisteredYet]: 'Not registered yet',
  [BusinessEntityType.Trusts]: 'Trust',
  [BusinessEntityType.UnincorporatedAssociations]: 'Unincorporated association',
}

export const ENTITY_TYPE_OPTIONS = Object.values(BusinessEntityType).map((entityType) => {
  return { value: entityType, label: ENTITY_TYPE_LABELS[entityType] }
})

/**
 * The wire values are `Partner` and `Customer`, which do not mean what they look like here:
 * `Partner` means GigSafe set the limit, `Customer` means the business asked for it. The values
 * are sent verbatim; only these labels are rewritten.
 */
export const LIMIT_SOURCE_LABELS: Record<RequestedLimitSource, string> = {
  [RequestedLimitSource.Partner]: 'GigSafe',
  [RequestedLimitSource.Customer]: 'Customer requested',
}

export const LIMIT_SOURCE_OPTIONS = Object.values(RequestedLimitSource).map((source) => {
  return { value: source, label: LIMIT_SOURCE_LABELS[source] }
})

export const SUPPLEMENTAL_DATA_TYPE_LABELS: Record<SupplementalDataType, string> = {
  [SupplementalDataType.BankStatements]: 'Bank statements',
  [SupplementalDataType.AccountingData]: 'Accounting data',
  [SupplementalDataType.BalanceSheet]: 'Balance sheet',
  [SupplementalDataType.ProfitLoss]: 'Profit & loss',
  [SupplementalDataType.Invoice]: 'Invoice',
  [SupplementalDataType.TermsDoc]: 'Terms document',
}

export const SUPPLEMENTAL_DATA_TYPE_OPTIONS = Object.values(SupplementalDataType).map((type) => {
  return { value: type, label: SUPPLEMENTAL_DATA_TYPE_LABELS[type] }
})

/**
 * `businessStartDateMonth` is a string of the month number, unpadded — para-node's e2e builder
 * writes `String(faker.number.int({ min: 1, max: 12 }))`. Never '01', never 'January'.
 */
export const MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

const BUSINESS_START_YEAR_RANGE = 60

/**
 * A descending range ending at the current year, stored as a 4-digit string. Built at module
 * load rather than per render so the array identity is stable for memoized children.
 */
export const BUSINESS_START_YEAR_OPTIONS = Array.from(
  { length: BUSINESS_START_YEAR_RANGE },
  (unused, index) => {
    const year = String(new Date().getFullYear() - index)

    return { value: year, label: year }
  },
)

/** The columns @GigsafeParsedCsv(AdminPartnerHistoricalInvoiceDto) validates on the server. */
export const HISTORICAL_INVOICE_CSV_COLUMNS = [
  'dueDate',
  'invoiceDate',
  'paymentDate',
  'payInvoiceOnDate',
  'amount',
  'description',
]

/**
 * The backend guard is `@GigsafeUploadedFile({ isRequired: true, fileType: 'pdf' })` — PDF only.
 * The admin-prototype's "PDF, CSV, or XLSX up to 25 MB" is invented; do not restore it.
 */
export const SUPPLEMENTAL_FILE_ACCEPT = 'application/pdf'

/**
 * The same PDF-only rule as an array, for `FileUploadInput`/`FormikFileInput`, which validate a
 * selected file's MIME type against a list rather than an `accept` string.
 */
export const SUPPLEMENTAL_FILE_ACCEPT_TYPES = ['application/pdf']

/**
 * Client-side upload size cap for both Business-tab file pickers, in bytes.
 *
 * The API documents no limit, so this is a product decision rather than a wire constraint — it
 * matches the 10MB cap the shared picker already applies elsewhere in this app. `FileUploadInput`
 * reports overage as `size / 1000000`MB, so the caption below is derived from the same number to
 * keep the copy and the check from drifting apart.
 */
export const BUSINESS_UPLOAD_FILE_SIZE_LIMIT = 10000000

const BUSINESS_UPLOAD_LIMIT_MB = BUSINESS_UPLOAD_FILE_SIZE_LIMIT / 1000000

export const SUPPLEMENTAL_FILE_CAPTION = `PDF (max. ${BUSINESS_UPLOAD_LIMIT_MB}MB)`
export const HISTORICAL_INVOICE_FILE_CAPTION = `CSV (max. ${BUSINESS_UPLOAD_LIMIT_MB}MB)`

/**
 * `@GigsafeParsedCsv` parses the historical-invoice upload server-side, so only a CSV is useful.
 * Browsers report CSV inconsistently — Excel-associated systems often send
 * `application/vnd.ms-excel` for a plain .csv — so all three are accepted here rather than
 * rejecting a genuine CSV on a MIME quirk. The server is the real validator.
 */
export const HISTORICAL_INVOICE_ACCEPT_TYPES = [
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
]

/**
 * `requestedCreditLimit` is sent in CENTS. The backend carries only @IsOptional() on it — no
 * @IsInt, no @Min — and passes it to Oatfi untouched, so nothing on the wire enforces the unit
 * and nothing will reject a dollars value. It is therefore stated in the label and echoed as
 * dollars in the field hint. See the design doc §7.1 before changing this.
 */
export const REQUESTED_CREDIT_LIMIT_UNIT_NOTE = 'cents'

/**
 * The storage ceiling for `requestedCreditLimit`, in cents.
 *
 * The Postgres column is `integer` (int4), so 2147483647 is the largest value that can be
 * stored — above it the write fails at the database rather than at a validator. The backend
 * carries no validation on this field at all (only `@IsOptional()`), so the form is the only
 * place the column's real capacity can be enforced before an admin hits a 500. It also bounds
 * the value well inside `Number.MAX_SAFE_INTEGER`, so the dollar echo cannot lose precision.
 */
export const MAX_REQUESTED_CREDIT_LIMIT_CENTS = 2147483647
